import dayjs from 'dayjs';
import { CallIntelligenceWritebackV1 } from '@frontend/api-call-intel';
import { i18next } from '@frontend/i18n';
import { IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { ChipVariants } from '@frontend/design-system';

export interface SummarySyncChipConfig {
  label: string;
  popoverChipLabel?: string;
  iconName: IconName | 'loader';
  color?: string;
  backgroundColor?: string;
  borderColor: string;
  chipVariant: ChipVariants;
  tooltipLabel?: string;
  disabled?: boolean;
}

export const getSummarySyncChipConfigMap = ({
  summarySyncStatus,
  isContactMissing,
  isSyncing,
  hasManualSyncAccess,
}: {
  summarySyncStatus?: CallIntelligenceWritebackV1.Types.WritebackStatus;
  isContactMissing?: boolean;
  isSyncing?: boolean;
  hasManualSyncAccess?: boolean;
}): SummarySyncChipConfig => {
  if (isSyncing) {
    return {
      label: i18next.t('Syncing...', { ns: 'call-intelligence' }),
      iconName: 'loader',
      borderColor: 'transparent',
      chipVariant: 'neutral',
    };
  }

  switch (summarySyncStatus) {
    case CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_READY_TO_SYNC: {
      const disabled = !hasManualSyncAccess || isContactMissing;
      return {
        label: i18next.t('Sync Summary', { ns: 'call-intelligence' }),
        iconName: 'data-sync-small',
        borderColor: disabled ? 'transparent' : theme.colors.neutral50,
        backgroundColor: disabled ? theme.colors.neutral5 : theme.colors.white,
        chipVariant: disabled ? 'disabled' : 'default',
        tooltipLabel: isContactMissing
          ? i18next.t('Create a patient record in your PMS or EHR to sync the summary', {
              ns: 'call-intelligence',
            })
          : !hasManualSyncAccess
          ? i18next.t('You do not have permission to manually sync', { ns: 'call-intelligence' })
          : undefined,
        disabled,
      };
    }
    case CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_SUCCEEDED:
      return {
        label: i18next.t('Synced', { ns: 'call-intelligence' }),
        iconName: 'success-filled-small',
        borderColor: theme.colors.success70,
        backgroundColor: theme.colors.success5,
        color: theme.colors.success70,
        chipVariant: 'success',
      };
    case CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_FAILED:
      return {
        label: i18next.t('Failed Sync', { ns: 'call-intelligence' }),
        popoverChipLabel: i18next.t('Failed', { ns: 'call-intelligence' }),
        iconName: 'error-filled-small',
        color: theme.colors.critical70,
        borderColor: theme.colors.critical70,
        chipVariant: 'critical',
        backgroundColor: theme.colors.critical5,
      };
    case CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_TRIGGERED:
    case CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_INPROGRESS:
      return {
        label: i18next.t('Syncing...', { ns: 'call-intelligence' }),
        iconName: 'loader',
        borderColor: 'transparent',
        chipVariant: 'neutral',
      };
    default:
      return {
        label: i18next.t('Sync Summary', { ns: 'call-intelligence' }),
        iconName: 'data-sync-small',
        borderColor: 'transparent',
        backgroundColor: theme.colors.neutral5,
        chipVariant: 'disabled',
        disabled: true,
      };
  }
};

const SYNC_DATE_FORMAT = 'MMM DD, YYYY hh:mm a';
const SYNC_TIME_FORMAT = 'hh:mm a';

export const formatSyncDate = (date: Date | string): string => {
  const dateObject = dayjs(typeof date === 'string' ? new Date(date) : date);
  const isSameDay = dayjs().isSame(dateObject, 'day');
  return dateObject.format(isSameDay ? SYNC_TIME_FORMAT : SYNC_DATE_FORMAT);
};

export const getSyncErrorMessage = ({
  summarySyncMode,
  summarySyncResponse,
}: {
  summarySyncResponse?: CallIntelligenceWritebackV1.Types.CallWritebackStatusIO['output'];
  summarySyncMode?: CallIntelligenceWritebackV1.Types.WritebackSettingMode;
}): string => {
  const prefixMessage =
    summarySyncMode === CallIntelligenceWritebackV1.Types.WritebackSettingMode.WRITEBACK_SETTING_MODE_MANUAL
      ? i18next.t('Manual sync failed.', { ns: 'call-intelligence' })
      : i18next.t('Automatic sync failed.', { ns: 'call-intelligence' });

  const pmsErrorMessage = summarySyncResponse?.errorMessage
    ? summarySyncResponse.errorMessage.replace(/\.?$/, '.') // Add full stop at the end if not present
    : '';

  const nextRetryMessage =
    summarySyncMode === CallIntelligenceWritebackV1.Types.WritebackSettingMode.WRITEBACK_SETTING_MODE_AUTOMATIC &&
    !!summarySyncResponse?.nextRetryAt
      ? i18next.t('Next auto-retry at {{time}}.', {
          time: formatSyncDate(summarySyncResponse.nextRetryAt),
          ns: 'call-intelligence',
        })
      : '';

  return [prefixMessage, pmsErrorMessage, nextRetryMessage].filter(Boolean).join(' ').trim();
};
